import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Users, CreditCard, CalendarDays, Wallet, Cake, Banknote, ArrowRight, Eye, EyeOff, Music, ExternalLink, Copy, Download, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
}

function setCookie(name, value, days = 365) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
}

function formatCash(value, hide) {
    if (hide) return '•••••';
    return `$${Number(value).toLocaleString('es-AR')}`;
}

export default function OwnerDashboard() {
    const [stats, setStats] = useState({ customers: 0, cards: 0, classes: 0, payments: 0, gym: null });
    const [birthdays, setBirthdays] = useState([]);
    const [pendingPayments, setPendingPayments] = useState(0);
    const [monthlyPayments, setMonthlyPayments] = useState(0);
    const [upcomingClasses, setUpcomingClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [hideCash, setHideCash] = useState(() => getCookie('hide_cash_panel') === '1');

    // Songs dialog state
    const [songsOpen, setSongsOpen] = useState(false);
    const [songsClass, setSongsClass] = useState(null);
    const [songs, setSongs] = useState([]);
    const [songsLoading, setSongsLoading] = useState(false);
    const [songsCopied, setSongsCopied] = useState(false);

    const toggleHideCash = () => {
        const next = !hideCash;
        setHideCash(next);
        setCookie('hide_cash_panel', next ? '1' : '0');
    };

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const { data } = await api.get('/owner/dashboard-summary');
                const payload = data.data;

                setStats({
                    gym: payload.gym,
                    customers: payload.counts?.customers ?? 0,
                    cards: payload.counts?.cards ?? 0,
                    classes: payload.counts?.classes ?? 0,
                    payments: payload.counts?.payments ?? 0,
                });
                setBirthdays(payload.birthdays_this_week || []);
                setPendingPayments(payload.pending_payments?.total_amount ?? 0);
                setMonthlyPayments(payload.monthly_payments_amount ?? 0);
                setUpcomingClasses(payload.upcoming_classes || []);
            } catch {
                // silently fail
            } finally {
                setLoading(false);
            }
        };
        fetchSummary();
    }, []);

    const openSongs = async (cls) => {
        setSongsClass(cls);
        setSongsOpen(true);
        setSongsLoading(true);
        try {
            const res = await api.get(`/owner/classes/${cls.id}/songs`);
            setSongs(res.data.data || []);
        } catch { setSongs([]); }
        finally { setSongsLoading(false); }
    };

    const formatSongsText = () => {
        if (!songsClass || songs.length === 0) return '';
        const header = `Canciones sugeridas — ${songsClass.name}`;
        const lines = songs.map((s, i) => {
            const linkLine = s.link ? `   Link: ${s.link}` : '';
            return `${i + 1}. ${s.title}${s.customer?.name ? ` (sugerida por ${s.customer.name})` : ''}${linkLine ? '\n' + linkLine : ''}`;
        });
        return [header, `${songs.length} cancione${songs.length !== 1 ? 's' : ''}`, '', ...lines].join('\n');
    };

    const handleCopySongs = async () => {
        const text = formatSongsText();
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setSongsCopied(true);
            setTimeout(() => setSongsCopied(false), 2000);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setSongsCopied(true);
            setTimeout(() => setSongsCopied(false), 2000);
        }
    };

    const handleDownloadSongs = () => {
        const text = formatSongsText();
        if (!text) return;
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `canciones-${songsClass?.name?.replace(/\s+/g, '-').toLowerCase() || 'clase'}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const cards = [
        { title: 'Alumnos', value: stats.customers, icon: Users },
        { title: 'Tarjetas', value: stats.cards, icon: CreditCard },
        { title: 'Clases', value: stats.classes, icon: CalendarDays },
        { title: 'Pagos', value: stats.payments, icon: Wallet },
    ];

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold uppercase tracking-wide text-white">{stats.gym?.name || 'Dashboard'}</h1>
                {stats.gym?.address && <p className="text-white/60 text-sm mt-1">{stats.gym.address}</p>}
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {cards.map((card) => (
                    <Card key={card.title} className="bg-[#0a0a0a] border-[#3a3a3f]">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-white/60 uppercase tracking-wider">{card.title}</CardTitle>
                            <card.icon className="h-5 w-5 text-white/60" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-bold text-white">{loading ? '...' : card.value}</div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Upcoming classes widgets */}
            {upcomingClasses.length > 0 && (
                <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                    {upcomingClasses.map((cls) => (
                        <Card key={cls.id} className={`bg-[#0a0a0a] border-[#3a3a3f] ${cls.cancelled ? 'opacity-70' : ''}`}>
                            <CardHeader className="flex flex-row items-center gap-3 pb-2">
                                <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                    <CalendarDays className="h-4 w-4 text-white" />
                                </div>
                                <div className="min-w-0">
                                    <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white truncate">
                                        {cls.name}
                                        {cls.cancelled && (
                                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-red-500/20 text-red-400 border border-red-500/30">
                                                Cancelada
                                            </span>
                                        )}
                                    </CardTitle>
                                    <p className="text-xs text-white/60">
                                        {cls.formatted_start || 'Sin horario'}
                                        {!cls.cancelled && (cls.max_capacity ? ` · ${cls.attendances_count ?? 0}/${cls.max_capacity} inscriptos` : ` · ${cls.attendances_count ?? 0} inscriptos`)}
                                    </p>
                                    {cls.cancelled && cls.cancellation_message && (
                                        <p className="text-xs text-red-300 mt-0.5">{cls.cancellation_message}</p>
                                    )}
                                </div>
                                <div className="ml-auto flex items-center gap-2 shrink-0">
                                    {!cls.cancelled && cls.is_current && (
                                        <span className="relative flex h-3 w-3" title="En curso">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                                        </span>
                                    )}
                                    {!cls.cancelled && cls.is_nearby && (
                                        <span className="relative flex h-3 w-3" title="Comienza en menos de 5 horas">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-yellow-500"></span>
                                        </span>
                                    )}
                                    {!cls.cancelled && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => openSongs(cls)}
                                            title="Ver canciones sugeridas"
                                            className="text-white hover:bg-white/5 h-8 w-8"
                                        >
                                            <Music className="h-4 w-4 text-white" />
                                        </Button>
                                    )}
                                </div>
                            </CardHeader>
                        </Card>
                    ))}
                </div>
            )}

            <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
                {/* Pending payments */}
                <Link to="/panel/pagos" className="block">
                    <Card className="bg-[#0a0a0a] border-[#3a3a3f] hover:border-white/20 transition-colors h-full">
                        <CardHeader className="flex flex-row items-center gap-3 pb-2">
                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                <Banknote className="h-4 w-4 text-white" />
                            </div>
                            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">Pagos pendientes</CardTitle>
                            <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleHideCash(); }}
                                className="ml-auto p-1 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                                title={hideCash ? 'Mostrar montos' : 'Ocultar montos'}
                            >
                                {hideCash ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between">
                                <div className="text-3xl font-bold text-white">{loading ? '...' : formatCash(pendingPayments, hideCash)}</div>
                                <ArrowRight className="h-5 w-5 text-white/40" />
                            </div>
                            <p className="text-xs text-white/40 mt-2">Promesas de efectivo y transferencias por confirmar.</p>
                        </CardContent>
                    </Card>
                </Link>

                {/* Monthly payments */}
                <Link to="/panel/pagos" className="block">
                    <Card className="bg-[#0a0a0a] border-[#3a3a3f] hover:border-white/20 transition-colors h-full">
                        <CardHeader className="flex flex-row items-center gap-3 pb-2">
                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                                <Wallet className="h-4 w-4 text-white" />
                            </div>
                            <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">Pagos del mes</CardTitle>
                            <button
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleHideCash(); }}
                                className="ml-auto p-1 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
                                title={hideCash ? 'Mostrar montos' : 'Ocultar montos'}
                            >
                                {hideCash ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                        </CardHeader>
                        <CardContent>
                            <div className="flex items-center justify-between">
                                <div className="text-3xl font-bold text-white">{loading ? '...' : formatCash(monthlyPayments, hideCash)}</div>
                                <ArrowRight className="h-5 w-5 text-white/40" />
                            </div>
                            <p className="text-xs text-white/40 mt-2">Total de pagos confirmados en el mes actual.</p>
                        </CardContent>
                    </Card>
                </Link>

                {/* Birthdays this week */}
                <Card className="bg-[#0a0a0a] border-[#3a3a3f]">
                    <CardHeader className="flex flex-row items-center gap-3 pb-2">
                        <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center">
                            <Cake className="h-4 w-4 text-white" />
                        </div>
                        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-white">Cumpleaños esta semana</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {birthdays.length === 0 ? (
                            <p className="text-sm text-white/40 text-center py-4">No hay cumpleaños esta semana.</p>
                        ) : (
                            <div className="space-y-2">
                                {birthdays.map((b) => (
                                    <div key={b.id} className="flex items-center justify-between py-2 border-b border-[#3a3a3f] last:border-0">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 bg-white/10 rounded-full flex items-center justify-center text-sm font-bold text-white">
                                                {b.first_name[0]}{b.last_name[0]}
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium text-white">{b.first_name} {b.last_name}</p>
                                                {b.gym_name && <p className="text-xs text-white/60">{b.gym_name}</p>}
                                            </div>
                                        </div>
                                        <span className="text-sm text-white/60 font-medium">{b.birth_date}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Songs Dialog */}
            <Dialog open={songsOpen} onOpenChange={setSongsOpen}>
                <DialogContent className="sm:max-w-lg bg-[#0a0a0a] border-[#3a3a3f] text-white">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-white uppercase tracking-wide">
                            <Music className="h-5 w-5 text-white" />
                            Canciones sugeridas — {songsClass?.name}
                        </DialogTitle>
                    </DialogHeader>
                    {songsLoading ? (
                        <p className="text-white/60 text-center py-6">Cargando...</p>
                    ) : songs.length === 0 ? (
                        <p className="text-white/60 text-center py-6">No hay canciones sugeridas para esta clase.</p>
                    ) : (
                        <div className="space-y-2 max-h-[400px] overflow-y-auto">
                            {songs.map(s => (
                                <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-[#3a3a3f] hover:bg-white/5">
                                    <div className="min-w-0">
                                        <p className="font-medium text-sm text-white truncate">{s.title}</p>
                                        <div className="flex items-center gap-2 text-xs text-white/60 mt-0.5">
                                            <span>{s.customer?.name || 'Alumno'}</span>
                                            {s.link && (
                                                <a
                                                    href={s.link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-blue-400 hover:text-blue-300 flex items-center gap-0.5"
                                                >
                                                    <ExternalLink className="h-3 w-3" />
                                                    Link
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-row justify-between items-center gap-3 mt-4">
                        <span className="text-sm text-white/60 shrink-0">
                            {songs.length} cancione{songs.length !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap justify-end">
                            {songs.length > 0 && (
                                <>
                                    <Button variant="outline" size="sm" onClick={handleCopySongs} className="border-[#3a3a3f] text-white hover:bg-white/5">
                                        {songsCopied ? <CheckCircle2 className="h-4 w-4 mr-1 text-green-400" /> : <Copy className="h-4 w-4 mr-1" />}
                                        {songsCopied ? 'Copiado' : 'Copiar'}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={handleDownloadSongs} className="border-[#3a3a3f] text-white hover:bg-white/5">
                                        <Download className="h-4 w-4 mr-1" />
                                        Descargar
                                    </Button>
                                </>
                            )}
                            <Button variant="outline" size="sm" onClick={() => setSongsOpen(false)} className="border-[#3a3a3f] text-white hover:bg-white/5">Cerrar</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
